import { ArrowLeft, Info } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useState, useEffect } from "react";
import { get } from "@/services/axiosUitils";
import welcomeBg from "../../assets/homebg.png";
import { CHART_AXIS_TICK_LG, CHART_FONT_SIZES } from "../../utils/chartConfig";

const StepsDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("weekly");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get selected date from FitnessPage navigation
  const selectedDate = location.state?.selectedDate || null;
  const selectedDateObj = location.state?.selectedDateObj || null;

  // API data states
  const [weeklyData, setWeeklyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [summaryData, setSummaryData] = useState({
    steps: 0,
    steps_goal: 10000,
    distance_km: 0,
    calories: 0,
    calories_burned: 0,
    floors: 0,
    days: 0,
    weeks: 0,
  });
  const [dateRange, setDateRange] = useState({
    start_date: "",
    end_date: "",
  });

  const formatNumber = (
    value: number | string | undefined,
    maximumFractionDigits = 0
  ) => {
    const n = Number(value || 0);
    return n.toLocaleString(undefined, { maximumFractionDigits });
  };

  const formatDateRange = () => {
    if (!dateRange.start_date || !dateRange.end_date) return "";
    const s = new Date(dateRange.start_date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    const e = new Date(dateRange.end_date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    return `${s} - ${e}`;
  };

  // Fetch steps data from API with selected date
  const fetchStepsData = async (type: "weekly" | "monthly") => {
    try {
      setLoading(true);
      setError(null);

      // Build API URL with date parameter if available
      const dateParam = selectedDate ? `&date=${selectedDate}` : "";
      const apiUrl = `fitbit_scan/steps-graph/?type=${type}${dateParam}`;

      const response = await get(apiUrl);

      // Handle both boolean and object success formats
      const isSuccess =
        typeof response.success === "boolean"
          ? response.success
          : response.success?.code === "Retrieved";

      if (isSuccess && response.summary) {
        setSummaryData(response.summary);
        setDateRange({
          start_date: response.start_date,
          end_date: response.end_date,
        });

        if (type === "weekly") {
          // Transform weekly API data to chart format
          const chartData = response.graph.map((item: any) => ({
            day: item.day,
            steps: item.value,
          }));
          setWeeklyData(chartData);
        } else {
          // Transform monthly API data to chart format
          const chartData = response.graph.map((item: any) => ({
            week: item.week,
            steps: item.value,
          }));
          setMonthlyData(chartData);
        }
      } else {
        setError("Failed to fetch steps data");
      }
    } catch (err: any) {
      setError(err.message || "An error occurred while fetching data");
    } finally {
      setLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    fetchStepsData("weekly");
    fetchStepsData("monthly");
  }, []);

  // Fetch data when tab changes
  const handleTabChange = (tab: "weekly" | "monthly") => {
    setActiveTab(tab);
    fetchStepsData(tab);
  };

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() =>
              navigate("/glucera/fitness", {
                state: {
                  selectedDate: selectedDate,
                  selectedDateObj: location.state?.selectedDateObj,
                },
              })
            }
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="text-center">
            <h1 className="text-title3 font-bold text-foreground">Steps</h1>
            {selectedDate ? (
              <p className="text-caption1 text-muted-foreground">
                {activeTab === "weekly" ? "Week of" : "Month of"}{" "}
                {new Date(selectedDate + "T00:00:00").toLocaleDateString()}
              </p>
            ) : (
              dateRange.start_date && (
                <p className="text-caption1 text-muted-foreground">
                  {new Date(dateRange.start_date).toLocaleDateString()} -{" "}
                  {new Date(dateRange.end_date).toLocaleDateString()}
                </p>
              )
            )}
          </div>
          <div className="w-10" />
        </div>

        {/* Today's Stats */}
        <Card className="rounded-2xl border border-border bg-gradient-to-br from-blue-50 to-blue-100 p-6 shadow-lg dark:from-blue-950 dark:to-blue-900">
          {loading ? (
            <div className="py-8 text-center">
              <div className="text-muted-foreground">Loading steps data...</div>
            </div>
          ) : error ? (
            <div className="py-8 text-center">
              <div className="mb-2 text-red-600">Failed to load data</div>
              <button
                onClick={() =>
                  fetchStepsData(activeTab as "weekly" | "monthly")
                }
                className="text-subhead text-blue-600 underline"
              >
                Try Again
              </button>
            </div>
          ) : (
            <div className="text-center">
              <p className="mb-2 text-footnote text-muted-foreground">
                Current Steps
              </p>
              <div className="mb-2 text-5xl font-bold text-foreground">
                {(summaryData?.steps || 0).toLocaleString()}
              </div>
              <p className="text-footnote text-muted-foreground">
                of {(summaryData?.steps_goal || 10000).toLocaleString()} goal
              </p>
              <div className="mt-4 h-2 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary dark:bg-primary"
                  style={{
                    width: `${Math.min((summaryData.steps / summaryData.steps_goal) * 100, 100)}%`,
                  }}
                />
              </div>
            </div>
          )}
        </Card>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-2">
          <Card className="rounded-xl border border-border bg-card p-3 text-center shadow-lg">
            <div className="text-title3 font-bold text-foreground">
              {summaryData.distance_km.toFixed(1)}
            </div>
            <div className="text-caption1 text-muted-foreground">km Distance</div>
          </Card>
          <Card className="rounded-xl border border-border bg-card p-3 text-center shadow-lg">
            <div className="text-title3 font-bold text-foreground">
              {summaryData.calories_burned || summaryData.calories || 0}
            </div>
            <div className="text-caption1 text-muted-foreground">Calories</div>
          </Card>
          <Card className="rounded-xl border border-border bg-card p-3 text-center shadow-lg">
            <div className="text-title3 font-bold text-foreground">
              {summaryData.floors}
            </div>
            <div className="text-caption1 text-muted-foreground">Floors</div>
          </Card>
        </div>

        {/* Tabs for Weekly/Monthly */}
        <Card className="overflow-hidden rounded-2xl border border-border bg-card shadow-lg">
          <div className="p-4">
            <div className="inline-flex w-full max-w-sm rounded-xl bg-muted p-1">
              <button
                onClick={() => handleTabChange("weekly")}
                className={`flex-1 rounded-lg px-4 py-2.5 text-callout font-medium transition-all duration-200 ${
                  activeTab === "weekly"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-background/50 hover:text-foreground"
                }`}
              >
                Weekly
              </button>
              <button
                onClick={() => handleTabChange("monthly")}
                className={`flex-1 rounded-lg px-4 py-2.5 text-callout font-medium transition-all duration-200 ${
                  activeTab === "monthly"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-background/50 hover:text-foreground"
                }`}
              >
                Monthly
              </button>
            </div>
          </div>

          <div className="overflow-hidden px-4 pb-4 pt-0">
            {activeTab === "weekly" && (
              <div className="w-full overflow-hidden">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-callout font-semibold text-foreground">
                    Weekly Steps ({weeklyData.length} {weeklyData.length === 1 ? "day" : "days"})
                    {dateRange.start_date && (
                      <span className="ml-2 text-footnote font-normal text-muted-foreground">
                        ({formatDateRange()})
                      </span>
                    )}
                  </h3>
                </div>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart
                    data={weeklyData}
                    margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="day" stroke="#6b7280" tick={CHART_AXIS_TICK_LG} />
                    <YAxis stroke="#6b7280" tick={CHART_AXIS_TICK_LG} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "2px solid hsl(var(--border))",
                        borderRadius: "12px",
                        color: "hsl(var(--foreground))",
                        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.2)",
                      }}
                      itemStyle={{ color: "hsl(var(--foreground))" }}
                      labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                      formatter={(value: any) => [
                        `${(Number(value) || 0).toLocaleString()} steps`,
                        "Steps",
                      ]}
                    />
                    <Bar dataKey="steps" fill="#2563eb" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {activeTab === "monthly" && (
              <div className="w-full overflow-hidden">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-callout font-semibold text-foreground">
                    Monthly Steps ({monthlyData.length}{" "}
                    {monthlyData.length === 1 ? "week" : "weeks"})
                    {dateRange.start_date && (
                      <span className="ml-2 text-footnote font-normal text-muted-foreground">
                        ({formatDateRange()})
                      </span>
                    )}
                  </h3>
                </div>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart
                    data={monthlyData}
                    margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="week" stroke="#6b7280" tick={CHART_AXIS_TICK_LG} />
                    <YAxis stroke="#6b7280" tick={CHART_AXIS_TICK_LG} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "2px solid hsl(var(--border))",
                        borderRadius: "12px",
                        color: "hsl(var(--foreground))",
                        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.2)",
                      }}
                      itemStyle={{ color: "hsl(var(--foreground))" }}
                      labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                      formatter={(value: any) => [
                        `${(Number(value) || 0).toLocaleString()} steps`,
                        "Steps",
                      ]}
                    />
                    <Bar dataKey="steps" fill="#2563eb" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </Card>

        {/* Information Card */}
        <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-950">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="mb-2 text-callout font-semibold text-foreground">
                How Steps Are Tracked
              </h3>
              <p className="text-callout leading-relaxed text-muted-foreground">
                Your steps are automatically tracked using your Fitbit
                device&apos;s accelerometer. The sensor detects motion patterns
                that indicate walking or running. Each step contributes to your
                daily activity goal and helps burn calories. Walking 10,000
                steps daily is recommended for maintaining good health and
                fitness.
              </p>
            </div>
          </div>
        </Card>

        {/* Benefits Card */}
        <Card className="rounded-2xl border border-border bg-gradient-to-br from-green-50 to-green-100 p-5 shadow-lg dark:from-green-950 dark:to-green-900">
          <h3 className="mb-3 text-callout font-semibold text-foreground">
            Health Benefits
          </h3>
          <ul className="space-y-2">
            {[
              "Improves cardiovascular health",
              "Helps maintain healthy weight",
              "Strengthens bones and muscles",
              "Boosts mood and energy levels",
              "Reduces risk of chronic diseases",
            ].map((benefit, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-callout text-muted-foreground"
              >
                <span className="mt-0.5 text-green-600 dark:text-green-400">
                  •
                </span>
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default StepsDetail;
